import {useEffect, useRef, useState} from "react";
import {KeyRound, Mail} from "lucide-react";
import {useForm} from "react-hook-form";
import {toast} from "sonner";
import {Button} from "../../../basic/button.tsx";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog.tsx";
import {Input} from "../../../basic/input.tsx";
import {Label} from "../../../basic/label.tsx";
import {Alert, AlertDescription} from "../../../basic/alert.tsx";
import {EmailCodeVerification} from "../../../shared/EmailCodeVerification.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {reauthenticate} from "../../../../services";
import {
    useCancelAlumniAcceptanceMutation,
    useConfirmAlumniAcceptanceMutation,
    useGetAlumniAcceptanceStatusQuery,
    useInitiateAlumniAcceptanceMutation
} from "../../../../store/generated/api";
import type {VwpEmailChangeResponse} from "../../../../store/generated/api";

interface AlumniDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

interface FormData {
    newEmail: string;
}

const OPEN_STATUSES = ["PENDING", "AWAITING_VERIFICATION"];
const RESEND_COOLDOWN_MS = 60_000;

function isOpen(change?: VwpEmailChangeResponse): boolean {
    return !!change?.status && OPEN_STATUSES.includes(change.status);
}

/**
 * Two-step alumni acceptance dialog. The step is derived from the open
 * acceptance change on the server, so reopening resumes mid-verification.
 */
export function AlumniDialog({open, onOpenChange, onSuccess}: AlumniDialogProps) {
    const {t} = useTranslationWithKeys();
    const {handleApiError, getLocalizedErrorMessage} = useErrorHandler();
    const [initiateAlumniAcceptance, {isLoading: isInitiating}] = useInitiateAlumniAcceptanceMutation();
    const [confirmAlumniAcceptance, {isLoading: isConfirming}] = useConfirmAlumniAcceptanceMutation();
    const [cancelAlumniAcceptance, {isLoading: isCancelling}] = useCancelAlumniAcceptanceMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [resendAvailableAt, setResendAvailableAt] = useState(0);
    const {register, handleSubmit, reset, formState: {errors}} = useForm<FormData>();

    const [pollStatus, setPollStatus] = useState(false);
    const {data: status, isError: statusError} = useGetAlumniAcceptanceStatusQuery(undefined, {
        skip: !open,
        pollingInterval: pollStatus ? 3000 : 0
    });

    const openChange = isOpen(status) ? status : undefined;
    useEffect(() => {
        setPollStatus(!!openChange);
    }, [openChange]);

    // Only react to a COMPLETED transition witnessed while open (e.g. confirmed in another tab).
    const initialStatus = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (status?.status !== undefined && initialStatus.current === undefined) {
            initialStatus.current = status.status;
        }
    }, [status?.status]);

    useEffect(() => {
        if (status?.status !== "COMPLETED" || initialStatus.current === "COMPLETED") {
            return;
        }
        if (isOpen(status)) {
            return;
        }
        toast.success(t('alumni.verification.success'));
        onOpenChange(false);
        onSuccess?.();
        reauthenticate({alumni: "activated"});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status?.status]);

    useEffect(() => {
        if (!open) {
            initialStatus.current = undefined;
        }
    }, [open]);

    const startAcceptance = async (newEmail: string) => {
        setErrorMessage(null);
        try {
            await initiateAlumniAcceptance({
                vwpAlumniAcceptInitiateRequest: {newEmail}
            }).unwrap();
            setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
        } catch (error: any) {
            setErrorMessage(getLocalizedErrorMessage(error));
        }
    };

    const handleFormSubmit = async (data: FormData) => {
        await startAcceptance(data.newEmail);
    };

    const handleConfirm = async (code: string) => {
        try {
            await confirmAlumniAcceptance({vwpAlumniAcceptConfirmRequest: {code}}).unwrap();
            // Prevent the polling effect from re-triggering on the refetched status.
            initialStatus.current = "COMPLETED";
            toast.success(t('alumni.verification.success'));
            onOpenChange(false);
            onSuccess?.();
            reauthenticate({alumni: "activated"});
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('alumni.verification.invalidCode')});
        }
    };

    const handleResend = async () => {
        // the license server rejects a second initiate while one is open — resend = cancel + re-initiate
        try {
            await cancelAlumniAcceptance().unwrap();
            await startAcceptance(openChange?.newEmail ?? "");
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('alumni.verification.resendError')});
        }
    };

    const handleCancel = async () => {
        try {
            await cancelAlumniAcceptance().unwrap();
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('alumni.verification.cancelError')});
        }
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            reset();
            setErrorMessage(null);
        }
        onOpenChange(open);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-md">
                {openChange ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('alumni.verification.title')}</DialogTitle>
                            <DialogDescription>{t('alumni.verification.description')}</DialogDescription>
                        </DialogHeader>
                        <EmailCodeVerification
                            newEmail={openChange.newEmail}
                            expiresAt={openChange.expiresAt}
                            statusError={statusError}
                            cancelling={isCancelling}
                            confirming={isConfirming}
                            resending={isCancelling || isInitiating}
                            resendAvailableAt={resendAvailableAt}
                            keyPrefix="alumni.verification"
                            onConfirm={handleConfirm}
                            onResend={handleResend}
                            onCancel={handleCancel}
                        />
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('alumni.dialogTitle')}</DialogTitle>
                            <DialogDescription>{t('alumni.dialogDescription')}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                            <div className="space-y-3">
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                    <Mail className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"/>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium mb-1">{t('alumni.points.emailTitle')}</p>
                                        <p className="text-sm text-muted-foreground">{t('alumni.points.emailText')}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                    <KeyRound className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"/>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium mb-1">{t('alumni.points.passwordTitle')}</p>
                                        <p className="text-sm text-muted-foreground">{t('alumni.points.passwordText')}</p>
                                    </div>
                                </div>
                            </div>
                            {errorMessage && (
                                <Alert variant="destructive">
                                    <AlertDescription>{errorMessage}</AlertDescription>
                                </Alert>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="newEmail">{t('alumni.newEmailLabel')}</Label>
                                <Input
                                    id="newEmail"
                                    type="email"
                                    placeholder={t('alumni.newEmailPlaceholder')}
                                    {...register('newEmail', {
                                        required: t('alumni.newEmailRequired'),
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: t('alumni.newEmailInvalid'),
                                        },
                                    })}
                                />
                                {errors.newEmail && (
                                    <p className="text-sm text-destructive">{errors.newEmail.message}</p>
                                )}
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                                    {t('common.cancel')}
                                </Button>
                                <Button type="submit" disabled={isInitiating}>
                                    {isInitiating
                                        ? t('alumni.sendingCode')
                                        : t('alumni.sendCodeButton')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
